import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import propertiesApi from "@/api/propertiesApi";
import PropertiesTable from "@/components/PropertiesTable/PropertiesTable";
import PropertyResponse from "@/model/PropertyResponse";
import "@/pages/PropertiesPage/PropertiesPage.scss";

const PAGE_SIZE = 20;

function PropertiesPage() {
  const { t } = useTranslation();
  const { session } = useAuth();

  const [properties, setProperties] = useState<PropertyResponse[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!session) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(false);

    propertiesApi
      .listAllProperties(session.accessToken, page, PAGE_SIZE)
      .then((result) => {
        if (cancelled) {
          return;
        }
        setProperties(result.content ?? []);
        setTotalPages(result.totalPages ?? 0);
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session, page]);

  if (!session) {
    return null;
  }

  return (
    <div className="properties-page">
      <header className="properties-page__header">
        <h1>{t("properties.list.title")}</h1>
        <p className="properties-page__subtitle">{t("properties.list.subtitle")}</p>
      </header>

      {isLoading && <p>{t("properties.panel.loading")}</p>}
      {!isLoading && error && <p role="alert">{t("properties.panel.error")}</p>}
      {!isLoading && !error && properties.length === 0 && (
        <p>{t("properties.list.empty")}</p>
      )}

      {!isLoading && !error && properties.length > 0 && (
        <>
          <PropertiesTable
            properties={properties}
            renderActions={(property) =>
              property.companyId && (
                <Link to={`/companies/${property.companyId}/properties`}>
                  {t("properties.list.viewCompanyLink")}
                </Link>
              )
            }
          />

          <div className="properties-page__pagination">
            <button
              type="button"
              onClick={() => setPage((current) => current - 1)}
              disabled={page <= 0}
            >
              {t("pagination.previous")}
            </button>
            <span>
              {t("pagination.pageInfo", { page: page + 1, totalPages })}
            </span>
            <button
              type="button"
              onClick={() => setPage((current) => current + 1)}
              disabled={page + 1 >= totalPages}
            >
              {t("pagination.next")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default PropertiesPage;
